// Format des dates de l'application : un réglage du cabinet, un seul formateur.
// `formatAppDate` lit le réglage courant (posé par `DateFormatSync`) ; il sert
// partout, y compris hors React. `formatDateWith` est la version pure.
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { create } from 'zustand';

export const DATE_FORMAT_STYLES = ['short', 'numeric', 'long'] as const;
export type DateFormatStyle = (typeof DATE_FORMAT_STYLES)[number];

export const DEFAULT_DATE_FORMAT: DateFormatStyle = 'short';

export const DATE_FORMAT_LABELS: Record<DateFormatStyle, string> = {
  short: 'Abrégé',
  numeric: 'Chiffres',
  long: 'Complet',
};

const PATTERNS: Record<DateFormatStyle, { full: string; noYear: string }> = {
  short: { full: 'd MMM yyyy', noYear: 'd MMM' },
  numeric: { full: 'dd/MM/yyyy', noYear: 'dd/MM' },
  long: { full: 'd MMMM yyyy', noYear: 'd MMMM' },
};

export function parseDateFormat(value: unknown): DateFormatStyle {
  return (DATE_FORMAT_STYLES as readonly unknown[]).includes(value)
    ? (value as DateFormatStyle)
    : DEFAULT_DATE_FORMAT;
}

export type DateInput = Date | string | number | null | undefined;

export interface FormatDateOptions {
  /** `false` : sans l'année (« 6 oct. »). Défaut : avec. */
  year?: boolean;
}

function toDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Version pure : rend '' pour une date absente ou illisible. */
export function formatDateWith(
  style: DateFormatStyle,
  value: DateInput,
  options: FormatDateOptions = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  const pattern = options.year === false ? PATTERNS[style].noYear : PATTERNS[style].full;
  return format(d, pattern, { locale: fr });
}

interface DateFormatState {
  style: DateFormatStyle;
  setStyle: (style: DateFormatStyle) => void;
}

export const useDateFormatStore = create<DateFormatState>((set) => ({
  style: DEFAULT_DATE_FORMAT,
  setStyle: (style) => set({ style }),
}));

/** Date au format du cabinet. */
export function formatAppDate(value: DateInput, options?: FormatDateOptions): string {
  return formatDateWith(useDateFormatStore.getState().style, value, options);
}
